export type RuntimePhase = 'idle' | 'starting' | 'ready' | 'stopped' | 'failed';

export type GameStatus =
  | 'aiming'
  | 'playing'
  | 'paused'
  | 'choosing-upgrade'
  | 'choosing-contract'
  | 'choosing-event'
  | 'choosing-cleanse'
  | 'won'
  | 'game-over';

export type BrickKind =
  'normal' | 'armored' | 'reinforced' | 'suppressor' | 'replicator' | 'fission';

export type CombatEffectId =
  | 'spawn-projectile'
  | 'split-projectile'
  | 'increase-spawn-count'
  | 'increase-split-count'
  | 'increase-damage'
  | 'increase-speed'
  | 'expand-paddle'
  | 'grant-life'
  | 'configure-ball-core';

export type UpgradeId =
  | 'echo-chamber'
  | 'fission-matrix'
  | 'kinetic-core'
  | 'overclock-drive'
  | 'wide-guard'
  | 'reserve-core'
  | 'arc-core'
  | 'arc-conductor'
  | 'phase-core'
  | 'phase-lance'
  | 'blast-core'
  | 'blast-shaping'
  | 'corrosion-core'
  | 'corrosion-catalyst'
  | 'gravity-core'
  | 'gravity-lens'
  | 'rebound-core'
  | 'rebound-capacitor';

export type BallCoreId = 'arc' | 'phase' | 'blast' | 'corrosion' | 'gravity' | 'rebound';

export type BallFusionId =
  | 'rail-storm'
  | 'thunderburst'
  | 'toxic-lance'
  | 'singularity-bomb'
  | 'orbital-return'
  | 'viral-echo';

export type RelicId =
  | 'prism-lattice'
  | 'kinetic-anchor'
  | 'overdrive-coil'
  | 'guardian-alloy'
  | 'reserve-heart'
  | 'harvest-lens'
  | 'duplication-seal'
  | 'fractal-chamber'
  | 'chronon-battery'
  | 'aegis-seed'
  | 'echo-counter'
  | 'singularity-crown'
  | 'cursed-reactor';

export type RunRewardKind = 'upgrade' | 'relic';

export type UpgradeCategory = 'foundation' | 'ball-core' | 'core-amplifier';

export type BuildId = 'swarm' | 'burst' | 'fortress';

export type BossPhaseId = 1 | 2 | 3;

export type BossMechanicId = 'aegis-nodes' | 'swarm-uplink' | 'singularity-field';

export type BossAffixId = 'armored-core' | 'kinetic-overclock' | 'resonant-defense';

export type BossSingularityStage = 'warning' | 'active' | 'cooldown';

export type BossSingularityPullSide = 'left' | 'right';

export type EliteProtocolId = 'fortress-matrix' | 'overclock-storm' | 'null-lockdown';

export type RiskContractId = 'siege-bounty' | 'redline-license' | 'null-dividend';

export type RunEventId = 'nanite-repair' | 'vitality-exchange' | 'echo-forge' | 'glass-overclock';

export type RunCurseId = 'glass-overclock' | 'reactor-instability';

export type RunCurseSource = 'event' | 'relic';

export type RunCleanseId = 'stability-purge';

export type RunEventAvailability =
  | 'available'
  | 'insufficient-score'
  | 'maximum-lives'
  | 'last-life'
  | 'upgrade-unavailable'
  | 'curse-slot-occupied'
  | 'unsafe-curse-combination';

export type RunRelicAvailability = 'available' | 'curse-slot-occupied' | 'unsafe-curse-combination';

export type ActiveAbilityId = 'nova-split' | 'aegis-field' | 'ion-pulse';

export const OBJECTIVE_CATALOG_VERSION = 1;

export const OBJECTIVE_IDS = [
  'active-operator',
  'boss-interceptor',
  'event-cartographer',
  'fusion-engineer',
  'curse-breaker',
  'thirty-wave-victor',
] as const;

export type ObjectiveId = (typeof OBJECTIVE_IDS)[number];

export type ObjectiveKind = 'mission' | 'achievement';

export type ObjectiveMetric =
  | 'ability-uses'
  | 'bosses-defeated'
  | 'events-resolved'
  | 'fusion-activated'
  | 'curses-cleansed'
  | 'full-runs-won';

export interface ObjectiveDefinition {
  readonly id: ObjectiveId;
  readonly kind: ObjectiveKind;
  readonly metric: ObjectiveMetric;
  readonly name: string;
  readonly description: string;
  readonly target: number;
  readonly progressLabel: string;
  readonly rewardLabel: string;
}

export const SEASON_CATALOG_VERSION = 1;

export const SEASON_IDS = ['voidline-s1'] as const;
export type SeasonId = (typeof SEASON_IDS)[number];

export const SEASON_CHALLENGE_IDS = [
  'skill-circuit',
  'boss-breach',
  'event-expedition',
  'fusion-trial',
] as const;
export type SeasonChallengeId = (typeof SEASON_CHALLENGE_IDS)[number];

export type SeasonChallengeMetric =
  'ability-uses' | 'bosses-defeated' | 'events-resolved' | 'completed-fusion-runs';

export interface SeasonChallengeDefinition {
  readonly id: SeasonChallengeId;
  readonly metric: SeasonChallengeMetric;
  readonly name: string;
  readonly description: string;
  readonly target: number;
  readonly progressLabel: string;
  readonly rewardLabel: string;
  readonly actionPath: '/levels' | '/challenges' | '/daily';
}

export interface SeasonDefinition {
  readonly id: SeasonId;
  readonly version: number;
  readonly name: string;
  readonly description: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly rotationDurationDays: number;
  readonly challengeIds: readonly SeasonChallengeId[];
}

export type CombatEffectSource = 'brick' | 'upgrade' | 'relic' | 'ability' | 'debug';

export interface CombatEffectTrigger {
  readonly effectId: CombatEffectId;
  readonly source: CombatEffectSource;
  readonly magnitude: number;
}

export interface BrickDefinition {
  readonly id: BrickKind;
  readonly name: string;
  readonly description: string;
  readonly hitPoints: number;
  readonly score: number;
  readonly tint: number;
  readonly secondaryDamageMultiplier: number;
  readonly rewardEffectId: CombatEffectId | null;
  readonly rewardMagnitude: number;
}

export interface UpgradeDefinition {
  readonly id: UpgradeId;
  readonly name: string;
  readonly description: string;
  readonly effectId: CombatEffectId;
  readonly magnitude: number;
  readonly maxStacks: number;
  readonly category: UpgradeCategory;
  readonly ballCoreId?: BallCoreId;
  readonly prerequisiteUpgradeIds?: readonly UpgradeId[];
}

export interface RelicDefinition {
  readonly id: RelicId;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly curseId?: RunCurseId;
  readonly rewardLabel?: string;
  readonly riskLabel?: string;
}

export interface BuildDefinition {
  readonly id: BuildId;
  readonly name: string;
  readonly description: string;
  readonly upgradeIds: readonly UpgradeId[];
}

export interface BossPhaseDefinition {
  readonly id: BossPhaseId;
  readonly name: string;
  readonly description: string;
  readonly movementSpeed: number;
  readonly tint: number;
}

export interface BossDefinition {
  readonly id: string;
  readonly name: string;
  readonly maxHitPoints: number;
  readonly width: number;
  readonly height: number;
  readonly phaseThresholds: readonly [number, number];
  readonly phases: readonly [BossPhaseDefinition, BossPhaseDefinition, BossPhaseDefinition];
}

export interface ActiveAbilityDefinition {
  readonly id: ActiveAbilityId;
  readonly name: string;
  readonly description: string;
  readonly shortcut: string;
  readonly cooldownMilliseconds: number;
  readonly durationMilliseconds: number;
}

export type CombatEvent =
  | {
      readonly type: 'brick-hit';
      readonly brickId: number;
      readonly brickKind: BrickKind;
      readonly damage: number;
      readonly remainingHitPoints: number;
    }
  | {
      readonly type: 'brick-destroyed';
      readonly brickId: number;
      readonly brickKind: BrickKind;
      readonly rewardEffectId: CombatEffectId | null;
    }
  | ({ readonly type: 'effect-triggered' } & CombatEffectTrigger)
  | {
      readonly type: 'upgrade-applied';
      readonly upgradeId: UpgradeId;
      readonly stack: number;
    }
  | {
      readonly type: 'build-selected';
      readonly buildId: BuildId;
    }
  | {
      readonly type: 'wave-cleared';
      readonly wave: number;
    }
  | {
      readonly type: 'upgrade-offered';
      readonly upgradeIds: readonly UpgradeId[];
    }
  | {
      readonly type: 'run-ended';
      readonly outcome: 'won' | 'lost';
      readonly wave: number;
    }
  | {
      readonly type: 'boss-phase-changed';
      readonly phase: BossPhaseId;
    }
  | {
      readonly type: 'boss-defeated';
    }
  | {
      readonly type: 'boss-shield-broken' | 'boss-shield-restored';
      readonly cycle: number;
    }
  | {
      readonly type: 'ability-used';
      readonly abilityId: ActiveAbilityId;
    };

export type GameFeedbackType =
  | 'launch'
  | 'audio-enabled'
  | 'brick-hit'
  | 'brick-destroyed'
  | 'reward-triggered'
  | 'ability-used'
  | 'boss-phase'
  | 'life-lost'
  | 'victory'
  | 'defeat';

export interface GameFeedbackEvent {
  readonly type: GameFeedbackType;
  readonly audioEnabled: boolean;
  readonly hapticsEnabled: boolean;
  readonly brickKind?: BrickKind;
}

export type GameCommand =
  | { type: 'launch' }
  | { type: 'toggle-pause' }
  | { type: 'restart' }
  | { type: 'add-projectile' }
  | { type: 'split-projectile' }
  | { type: 'toggle-stress-test' }
  | { type: 'select-build'; buildId: BuildId }
  | { type: 'select-upgrade'; upgradeId: UpgradeId }
  | { type: 'select-relic'; relicId: RelicId }
  | { type: 'reroll-reward' }
  | { type: 'skip-reward' }
  | { type: 'continue-without-upgrade' }
  | { type: 'resolve-risk-contract'; accepted: boolean }
  | { type: 'resolve-run-event'; accepted: boolean }
  | { type: 'resolve-run-cleanse'; cleansed: boolean }
  | { type: 'use-active-ability'; abilityId: ActiveAbilityId }
  | { type: 'toggle-audio' }
  | { type: 'toggle-haptics' };

export type EffectsQuality = 'full' | 'reduced' | 'minimal';

export type RunPhase =
  | 'combat'
  | 'choosing-upgrade'
  | 'choosing-contract'
  | 'choosing-event'
  | 'choosing-cleanse'
  | 'won'
  | 'lost';

export type UpgradeStacks = Readonly<Partial<Record<UpgradeId, number>>>;

export interface RunStats {
  readonly wavesCleared: number;
  readonly bricksDestroyed: number;
  readonly upgradesChosen: number;
  readonly relicsChosen: number;
  readonly rerollsUsed: number;
  readonly rewardsSkipped: number;
  readonly rewardsExhausted: number;
  readonly livesLost: number;
  readonly peakProjectiles: number;
  readonly elapsedSeconds: number;
}

export interface GameplayChallengeContext {
  readonly challengeId: 'build-mastery';
  readonly challengeRulesVersion: number;
  readonly challengeContentVersion: number;
}

export type GameplayAnalyticsContext = {
  readonly runId: string;
  readonly wave: number;
  readonly startingWave: number;
  readonly maxWaves: number;
  readonly buildId: BuildId;
} & (
  | GameplayChallengeContext
  | {
      readonly challengeId?: never;
      readonly challengeRulesVersion?: never;
      readonly challengeContentVersion?: never;
    }
);

export type RunEndReason = 'won' | 'lost' | 'restarted' | 'exited';

export type GameplayAnalyticsEvent =
  | (GameplayAnalyticsContext & { readonly type: 'run-started' })
  | (GameplayAnalyticsContext & {
      readonly type: 'wave-started';
      readonly brickCount: number;
      readonly boss: boolean;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'wave-completed';
      readonly durationMilliseconds: number;
      readonly scoreGained: number;
      readonly bricksDestroyed: number;
      readonly livesRemaining: number;
      readonly peakProjectiles: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'reward-offered';
      readonly rewardKind: RunRewardKind;
      readonly choiceIds: readonly (UpgradeId | RelicId)[];
      readonly exhausted: boolean;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'reward-action';
      readonly rewardKind: RunRewardKind;
      readonly action: 'selected' | 'rerolled' | 'skipped' | 'auto-recovered';
      readonly selectedId?: UpgradeId | RelicId;
      readonly choiceIds?: readonly (UpgradeId | RelicId)[];
      readonly recovery?: 'life' | 'score';
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'contract-offered';
      readonly contractId: RiskContractId;
      readonly startWave: number;
      readonly endWave: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'contract-action';
      readonly contractId: RiskContractId;
      readonly action: 'accepted' | 'declined';
      readonly startWave: number;
      readonly endWave: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'run-event-offered';
      readonly runEventId: RunEventId;
      readonly affordable: boolean;
      readonly targetUpgradeId?: UpgradeId;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'run-event-action';
      readonly runEventId: RunEventId;
      readonly action: 'accepted' | 'declined';
      readonly scoreBefore: number;
      readonly scoreAfter: number;
      readonly livesBefore: number;
      readonly livesAfter: number;
      readonly targetUpgradeId?: UpgradeId;
      readonly copiedUpgradeStack?: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'run-curse-status';
      readonly curseId: RunCurseId;
      readonly source: RunCurseSource;
      readonly sourceRelicId?: RelicId;
      readonly action: 'activated' | 'cleansed' | 'expired';
      readonly startWave: number;
      readonly endWave: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'run-cleanse-offered';
      readonly cleanseId: RunCleanseId;
      readonly curseId: RunCurseId;
      readonly source: RunCurseSource;
      readonly sourceRelicId?: RelicId;
      readonly remainingWaves: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'run-cleanse-action';
      readonly cleanseId: RunCleanseId;
      readonly curseId: RunCurseId;
      readonly source: RunCurseSource;
      readonly sourceRelicId?: RelicId;
      readonly action: 'cleansed' | 'retained';
      readonly remainingWaves: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'build-selected';
      readonly selectedBuildId: BuildId;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'ability-used';
      readonly abilityId: ActiveAbilityId;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'boss-mechanic';
    } & (
        | {
            readonly mechanicId: 'aegis-nodes';
            readonly action: 'shield-broken' | 'shield-restored';
            readonly cycle: number;
          }
        | {
            readonly mechanicId: 'swarm-uplink';
            readonly action: 'reinforcement-summoned' | 'reinforcement-destroyed';
            readonly sequence: number;
            readonly activeCount: number;
          }
        | {
            readonly mechanicId: 'singularity-field';
            readonly action: 'field-warned' | 'field-activated' | 'field-ended';
            readonly cycle: number;
            readonly pullSide: BossSingularityPullSide;
          }
      ))
  | (GameplayAnalyticsContext & {
      readonly type: 'life-lost';
      readonly livesRemaining: number;
    })
  | (GameplayAnalyticsContext & {
      readonly type: 'run-ended';
      readonly reason: RunEndReason;
      readonly score: number;
      readonly stats: RunStats;
      readonly upgradeIds: readonly UpgradeId[];
      readonly relicIds: readonly RelicId[];
      readonly ballCoreIds: readonly BallCoreId[];
      readonly ballFusionIds: readonly BallFusionId[];
      readonly challengeCompleted?: boolean;
    });

export interface RunProgressionSnapshot {
  readonly seed: string;
  readonly wave: number;
  readonly maxWaves: number;
  readonly phase: RunPhase;
  readonly rewardKind: RunRewardKind;
  readonly upgradeChoices: readonly UpgradeId[];
  readonly relicChoices: readonly RelicId[];
  readonly activeRelicIds: readonly RelicId[];
  readonly rewardRerollAvailable: boolean;
  readonly upgradeStacks: UpgradeStacks;
  readonly pendingRiskContract: RiskContractSnapshot | null;
  readonly activeRiskContract: RiskContractSnapshot | null;
  readonly pendingRunEvent: RunEventSnapshot | null;
  readonly pendingRunCleanse: RunCleanseSnapshot | null;
  readonly activeRunCurse: RunCurseSnapshot | null;
  readonly stats: RunStats;
}

export interface BossSnapshot {
  readonly active: boolean;
  readonly phase: BossPhaseId;
  readonly hitPoints: number;
  readonly maxHitPoints: number;
  readonly healthRatio: number;
}

export interface BossAffixSnapshot {
  readonly id: BossAffixId;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly riskLabel: string;
  readonly badgeLabel: string;
  readonly hitPointMultiplier: number;
  readonly movementSpeedMultiplier: number;
  readonly incomingDamageMultiplier: number;
  readonly mechanicCadenceMultiplier: number;
}

export interface BossShieldSnapshot {
  readonly active: boolean;
  readonly cycle: number;
  readonly nodesRemaining: number;
  readonly nodeHitPoints: readonly number[];
  readonly maxNodeHitPoints: number;
  readonly vulnerableRemainingMilliseconds: number;
  readonly vulnerableDurationMilliseconds: number;
}

export interface BossSummonSnapshot {
  readonly phase: BossPhaseId;
  readonly reinforcementsActive: number;
  readonly reinforcementCapacity: number;
  readonly summonSequence: number;
  readonly summonRemainingMilliseconds: number;
  readonly summonIntervalMilliseconds: number;
  readonly bossDamageMultiplier: number;
}

export interface BossSingularitySnapshot {
  readonly phase: BossPhaseId;
  readonly stage: BossSingularityStage;
  readonly cycle: number;
  readonly pullSide: BossSingularityPullSide;
  readonly remainingMilliseconds: number;
  readonly stageDurationMilliseconds: number;
  readonly steeringRadiansPerSecond: number;
  readonly aegisSteeringMultiplier: number;
}

export interface EliteWaveSnapshot {
  readonly id: EliteProtocolId;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly riskLabel: string;
  readonly rewardLabel: string;
  readonly brickHitPointMultiplier: number;
  readonly projectileSpeedMultiplier: number;
  readonly secondaryDamageMultiplier: number;
  readonly scoreMultiplier: number;
  readonly rewardChoiceCount: number;
}

export interface RiskContractSnapshot {
  readonly id: RiskContractId;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly riskLabel: string;
  readonly rewardLabel: string;
  readonly startWave: number;
  readonly endWave: number;
  readonly brickHitPointMultiplier: number;
  readonly projectileSpeedMultiplier: number;
  readonly directDamageMultiplier: number;
  readonly secondaryDamageMultiplier: number;
  readonly scoreMultiplier: number;
  readonly abilityCooldownRateMultiplier: number;
  readonly rewardChoiceBonus: number;
}

export interface RunEventSnapshot {
  readonly id: RunEventId;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly costLabel: string;
  readonly rewardLabel: string;
  readonly scoreDelta: number;
  readonly lifeDelta: number;
  readonly duplicateUpgradeId: UpgradeId | null;
  readonly duplicateUpgradeMaximumStacks: number | null;
  readonly activatesCurse: RunCurseSnapshot | null;
}

export interface RunCurseSnapshot {
  readonly id: RunCurseId;
  readonly source: RunCurseSource;
  readonly sourceRelicId: RelicId | null;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly riskLabel: string;
  readonly rewardLabel: string;
  readonly startWave: number;
  readonly endWave: number;
  readonly damageMultiplier: number;
  readonly paddleWidthMultiplier: number;
}

export interface RunCleanseSnapshot {
  readonly id: RunCleanseId;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly costLabel: string;
  readonly rewardLabel: string;
  readonly clearedWave: number;
  readonly remainingWaves: number;
  readonly removesRelicId: RelicId | null;
  readonly curse: RunCurseSnapshot;
}

export interface RunCleanseResolution {
  readonly cleanseId: RunCleanseId;
  readonly cleansed: boolean;
  readonly cleansedCurse: RunCurseSnapshot | null;
  readonly removedRelicId: RelicId | null;
}

export interface RunEventResources {
  readonly score: number;
  readonly lives: number;
  readonly maximumLives: number;
}

export interface RunEventResolution {
  readonly eventId: RunEventId;
  readonly accepted: boolean;
  readonly score: number;
  readonly lives: number;
  readonly duplicatedUpgradeId: UpgradeId | null;
  readonly activatedCurse: RunCurseSnapshot | null;
}

export interface ActiveAbilityState {
  readonly ready: boolean;
  readonly remainingMilliseconds: number;
  readonly cooldownMilliseconds: number;
}

export type ActiveAbilityStates = Readonly<Record<ActiveAbilityId, ActiveAbilityState>>;

export interface GameHudSnapshot {
  readonly status: GameStatus;
  readonly score: number;
  readonly lives: number;
  readonly maximumLives: number;
  readonly wave: number;
  readonly bricksRemaining: number;
  readonly aimOffsetDegrees: number;
  readonly projectilesActive: number;
  readonly projectileCapacity: number;
  readonly effectsQuality: EffectsQuality;
  readonly stressMode: boolean;
  readonly activeBuildId: BuildId;
  readonly activeUpgradeIds: readonly UpgradeId[];
  readonly activeBallCoreIds: readonly BallCoreId[];
  readonly activeBallFusionIds: readonly BallFusionId[];
  readonly activeRelicIds: readonly RelicId[];
  readonly canSelectBuild: boolean;
  readonly lastEffectId: CombatEffectId | null;
  readonly specialBricksRemaining: number;
  readonly maxWaves: number;
  readonly runSeed: string;
  readonly upgradeChoices: readonly UpgradeId[];
  readonly rewardKind: RunRewardKind;
  readonly relicChoices: readonly RelicId[];
  readonly rewardRerollAvailable: boolean;
  readonly upgradeStacks: UpgradeStacks;
  readonly runStats: RunStats;
  readonly boss: BossSnapshot | null;
  readonly bossAffix: BossAffixSnapshot | null;
  readonly bossShield: BossShieldSnapshot | null;
  readonly bossSummon: BossSummonSnapshot | null;
  readonly bossSingularity: BossSingularitySnapshot | null;
  readonly elite: EliteWaveSnapshot | null;
  readonly pendingRiskContract: RiskContractSnapshot | null;
  readonly activeRiskContract: RiskContractSnapshot | null;
  readonly pendingRunEvent: RunEventSnapshot | null;
  readonly pendingRunCleanse: RunCleanseSnapshot | null;
  readonly activeRunCurse: RunCurseSnapshot | null;
  readonly runEventAvailability: RunEventAvailability | null;
  readonly abilityStates: ActiveAbilityStates;
  readonly aegisRemainingMilliseconds: number;
  readonly audioEnabled: boolean;
  readonly hapticsEnabled: boolean;
}

export interface Velocity {
  readonly x: number;
  readonly y: number;
}

export interface PaddleBounceInput {
  readonly ballX: number;
  readonly paddleX: number;
  readonly paddleWidth: number;
  readonly paddleVelocityX: number;
  readonly speed: number;
  readonly maximumAngleDegrees?: number;
  readonly minimumVerticalSpeed?: number;
}

export interface ProjectileTrajectoryInput {
  readonly velocity: Velocity;
  readonly speed: number;
  readonly minimumVerticalRatio?: number;
  readonly minimumHorizontalRatio?: number;
  readonly fallbackVerticalDirection?: -1 | 1;
  readonly fallbackHorizontalDirection?: -1 | 1;
}

export interface SingularitySteeringInput extends ProjectileTrajectoryInput {
  readonly pullSide: BossSingularityPullSide;
  readonly steeringRadiansPerSecond: number;
  readonly deltaMilliseconds: number;
  readonly steeringMultiplier?: number;
}

export interface RadialBurstInput {
  readonly count: number;
  readonly speed: number;
  readonly headingRadians?: number;
}

export interface ProjectileSpawnLimitInput {
  readonly requestedCount: number;
  readonly activeCount: number;
  readonly replacingCount: number;
  readonly softLimit: number;
  readonly hardLimit: number;
  readonly allowBeyondSoftLimit: boolean;
}

export interface ProjectileDrainInput {
  readonly activeProjectileCount: number;
  readonly bricksRemaining: number;
  readonly stressMode: boolean;
}

export const MAXIMUM_AIM_OFFSET_DEGREES = 55;

export interface GameSessionState {
  readonly phase: 'boot';
  readonly score: number;
  readonly wave: number;
}

export interface CampaignProgress {
  readonly highestCompletedLevel: number;
  readonly selectedLevel: number;
}

export function createInitialGameSession(): GameSessionState {
  return {
    phase: 'boot',
    score: 0,
    wave: 0,
  };
}

export function createInitialCampaignProgress(): CampaignProgress {
  return {
    highestCompletedLevel: 0,
    selectedLevel: 1,
  };
}

export function normalizeCampaignProgress(
  progress: Partial<CampaignProgress> | null | undefined,
  maxLevels: number,
): CampaignProgress {
  const safeMaxLevels = Math.max(1, toFiniteInteger(maxLevels, 1));
  const highestCompletedLevel = clamp(
    toFiniteInteger(progress?.highestCompletedLevel, 0),
    0,
    safeMaxLevels,
  );
  const highestUnlockedLevel = Math.min(safeMaxLevels, highestCompletedLevel + 1);
  const selectedLevel = clamp(toFiniteInteger(progress?.selectedLevel, 1), 1, highestUnlockedLevel);

  return { highestCompletedLevel, selectedLevel };
}

export function isCampaignLevelUnlocked(
  progress: CampaignProgress,
  level: number,
  maxLevels: number,
): boolean {
  if (!Number.isInteger(level) || level < 1 || level > Math.floor(maxLevels)) {
    return false;
  }
  const normalized = normalizeCampaignProgress(progress, maxLevels);
  return Math.floor(level) <= Math.min(maxLevels, normalized.highestCompletedLevel + 1);
}

export function selectCampaignLevel(
  progress: CampaignProgress,
  level: number,
  maxLevels: number,
): CampaignProgress {
  const normalized = normalizeCampaignProgress(progress, maxLevels);
  if (!isCampaignLevelUnlocked(normalized, level, maxLevels)) {
    return normalized;
  }
  return { ...normalized, selectedLevel: Math.floor(level) };
}

export function completeCampaignLevel(
  progress: CampaignProgress,
  level: number,
  maxLevels: number,
): CampaignProgress {
  const normalized = normalizeCampaignProgress(progress, maxLevels);
  if (!Number.isInteger(level)) {
    return normalized;
  }
  const completedLevel = level;
  if (
    completedLevel < 1 ||
    completedLevel > Math.floor(maxLevels) ||
    completedLevel !== normalized.highestCompletedLevel + 1
  ) {
    return normalized;
  }
  return { ...normalized, highestCompletedLevel: completedLevel };
}

function toFiniteInteger(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : fallback;
}

export function createInitialHudSnapshot(
  bricksRemaining: number,
  lives: number,
  projectileCapacity = 96,
  maxWaves = 10,
  startingWave = 1,
): GameHudSnapshot {
  const safeMaxWaves = Math.max(1, Math.floor(maxWaves));
  return {
    status: 'aiming',
    score: 0,
    lives,
    maximumLives: Math.max(1, lives),
    wave: clamp(Math.floor(startingWave), 1, safeMaxWaves),
    bricksRemaining,
    aimOffsetDegrees: 0,
    projectilesActive: 1,
    projectileCapacity,
    effectsQuality: 'full',
    stressMode: false,
    activeBuildId: 'swarm',
    activeUpgradeIds: [],
    activeBallCoreIds: [],
    activeBallFusionIds: [],
    activeRelicIds: [],
    canSelectBuild: true,
    lastEffectId: null,
    specialBricksRemaining: 0,
    maxWaves: safeMaxWaves,
    runSeed: '',
    upgradeChoices: [],
    rewardKind: 'upgrade',
    relicChoices: [],
    rewardRerollAvailable: false,
    upgradeStacks: {},
    runStats: createInitialRunStats(),
    boss: null,
    bossAffix: null,
    bossShield: null,
    bossSummon: null,
    bossSingularity: null,
    elite: null,
    pendingRiskContract: null,
    activeRiskContract: null,
    pendingRunEvent: null,
    pendingRunCleanse: null,
    activeRunCurse: null,
    runEventAvailability: null,
    abilityStates: createReadyAbilityStates(),
    aegisRemainingMilliseconds: 0,
    audioEnabled: true,
    hapticsEnabled: true,
  };
}

export function createReadyAbilityStates(): ActiveAbilityStates {
  return {
    'nova-split': {
      ready: true,
      remainingMilliseconds: 0,
      cooldownMilliseconds: 12_000,
    },
    'aegis-field': {
      ready: true,
      remainingMilliseconds: 0,
      cooldownMilliseconds: 20_000,
    },
    'ion-pulse': {
      ready: true,
      remainingMilliseconds: 0,
      cooldownMilliseconds: 24_000,
    },
  };
}

export function createInitialRunStats(): RunStats {
  return {
    wavesCleared: 0,
    bricksDestroyed: 0,
    upgradesChosen: 0,
    relicsChosen: 0,
    rerollsUsed: 0,
    rewardsSkipped: 0,
    rewardsExhausted: 0,
    livesLost: 0,
    peakProjectiles: 1,
    elapsedSeconds: 0,
  };
}

export function createRunSeed(timestamp: number, nonce = 0): string {
  const safeTimestamp = Math.max(0, Math.floor(timestamp));
  const safeNonce = Math.max(0, Math.floor(nonce));
  return `RB-${safeTimestamp.toString(36).toUpperCase()}-${safeNonce.toString(36).toUpperCase()}`;
}

export function shuffleWithSeed<T>(items: readonly T[], seed: string): T[] {
  const shuffled = [...items];
  const random = createSeededRandom(seed);
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const current = shuffled[index];
    shuffled[index] = shuffled[swapIndex]!;
    shuffled[swapIndex] = current!;
  }
  return shuffled;
}

export interface SeededUpgradeChoicesInput {
  readonly runSeed: string;
  readonly clearedWave: number;
  readonly upgradeIds: readonly UpgradeId[];
  readonly upgradeStacks: UpgradeStacks;
  readonly maximumStacks: Readonly<Partial<Record<UpgradeId, number>>>;
  readonly count?: number;
  readonly rerollIndex?: number;
}

export function selectSeededUpgradeChoices(input: SeededUpgradeChoicesInput): UpgradeId[] {
  const eligibleUpgradeIds = input.upgradeIds.filter((upgradeId) => {
    const currentStack = input.upgradeStacks[upgradeId] ?? 0;
    const maximumStack = input.maximumStacks[upgradeId] ?? Number.POSITIVE_INFINITY;
    return currentStack < maximumStack;
  });
  const count = clamp(Math.floor(input.count ?? 3), 0, eligibleUpgradeIds.length);
  return shuffleWithSeed(
    eligibleUpgradeIds,
    `${input.runSeed}:reward:${Math.max(1, Math.floor(input.clearedWave))}:reroll:${Math.max(
      0,
      Math.floor(input.rerollIndex ?? 0),
    )}`,
  ).slice(0, count);
}

interface RunProgressionOptions {
  readonly seed: string;
  readonly maxWaves: number;
  readonly startingWave?: number;
  readonly initialUpgradeIds: readonly UpgradeId[];
  readonly createUpgradeChoices: (
    clearedWave: number,
    upgradeStacks: UpgradeStacks,
    rerollIndex?: number,
  ) => readonly UpgradeId[];
  readonly bossRewardWave?: number;
  readonly bossRewardWaves?: readonly number[];
  readonly createRelicChoices?: (
    clearedWave: number,
    activeRelicIds: readonly RelicId[],
    rerollIndex?: number,
    activeCurse?: RunCurseSnapshot | null,
  ) => readonly RelicId[];
  readonly createRelicCurse?: (relicId: RelicId, clearedWave: number) => RunCurseSnapshot | null;
  readonly createRiskContractOffer?: (clearedWave: number) => RiskContractSnapshot | null;
  readonly createRunEventOffer?: (
    clearedWave: number,
    upgradeStacks: UpgradeStacks,
  ) => RunEventSnapshot | null;
  readonly createRunCleanseOffer?: (
    clearedWave: number,
    activeCurse: RunCurseSnapshot | null,
  ) => RunCleanseSnapshot | null;
  readonly protectedRunCurseWaves?: readonly number[];
}

function cloneRunCurse(curse: RunCurseSnapshot | null): RunCurseSnapshot | null {
  return curse ? { ...curse } : null;
}

function cloneRunEvent(event: RunEventSnapshot | null): RunEventSnapshot | null {
  return event
    ? {
        ...event,
        activatesCurse: cloneRunCurse(event.activatesCurse),
      }
    : null;
}

function cloneRunCleanse(cleanse: RunCleanseSnapshot | null): RunCleanseSnapshot | null {
  return cleanse
    ? {
        ...cleanse,
        curse: { ...cleanse.curse },
      }
    : null;
}

function isValidRunCleanseOffer(
  cleanse: RunCleanseSnapshot,
  activeCurse: RunCurseSnapshot | null,
  activeRelicIds: readonly RelicId[],
  clearedWave: number,
): boolean {
  const expectedRelicId = activeCurse?.source === 'relic' ? activeCurse.sourceRelicId : null;
  return Boolean(
    activeCurse &&
    cleanse.id === 'stability-purge' &&
    cleanse.clearedWave === clearedWave &&
    cleanse.remainingWaves === activeCurse.endWave - clearedWave &&
    cleanse.remainingWaves >= 1 &&
    cleanse.removesRelicId === expectedRelicId &&
    (expectedRelicId === null || activeRelicIds.includes(expectedRelicId)) &&
    cleanse.curse.id === activeCurse.id &&
    cleanse.curse.source === activeCurse.source &&
    cleanse.curse.sourceRelicId === activeCurse.sourceRelicId &&
    cleanse.curse.startWave === activeCurse.startWave &&
    cleanse.curse.endWave === activeCurse.endWave,
  );
}

export const RUN_EVENT_CURSE_SAFETY_LIMITS = {
  maximumDurationWaves: 3,
  maximumDamageMultiplier: 1.5,
  minimumPaddleWidthMultiplier: 0.72,
} as const;

export const RUN_RELIC_CURSE_SAFETY_LIMITS = {
  maximumDurationWaves: 3,
  minimumPaddleWidthMultiplier: 0.72,
} as const;

export function getRunRelicAvailability(
  relicId: RelicId,
  attachedCurse: RunCurseSnapshot | null,
  activeRunCurse: RunCurseSnapshot | null = null,
  protectedRunCurseWaves: readonly number[] = [],
): RunRelicAvailability {
  if (!attachedCurse) {
    return relicId === 'cursed-reactor' ? 'unsafe-curse-combination' : 'available';
  }
  if (activeRunCurse) {
    return 'curse-slot-occupied';
  }
  const duration = attachedCurse.endWave - attachedCurse.startWave + 1;
  const invalid =
    relicId !== 'cursed-reactor' ||
    attachedCurse.id !== 'reactor-instability' ||
    attachedCurse.source !== 'relic' ||
    attachedCurse.sourceRelicId !== relicId ||
    !Number.isInteger(attachedCurse.startWave) ||
    !Number.isInteger(attachedCurse.endWave) ||
    attachedCurse.startWave < 1 ||
    duration !== RUN_RELIC_CURSE_SAFETY_LIMITS.maximumDurationWaves ||
    !Number.isFinite(attachedCurse.damageMultiplier) ||
    attachedCurse.damageMultiplier !== 1 ||
    !Number.isFinite(attachedCurse.paddleWidthMultiplier) ||
    attachedCurse.paddleWidthMultiplier !==
      RUN_RELIC_CURSE_SAFETY_LIMITS.minimumPaddleWidthMultiplier ||
    protectedRunCurseWaves.some(
      (wave) =>
        Number.isInteger(wave) && wave >= attachedCurse.startWave && wave <= attachedCurse.endWave,
    );
  return invalid ? 'unsafe-curse-combination' : 'available';
}

export function getRunEventAvailability(
  event: RunEventSnapshot,
  resources: RunEventResources,
  upgradeStacks: UpgradeStacks,
  activeRunCurse: RunCurseSnapshot | null = null,
  protectedRunCurseWaves: readonly number[] = [],
): RunEventAvailability {
  if (!Number.isFinite(resources.score) || resources.score + event.scoreDelta < 0) {
    return 'insufficient-score';
  }
  if (
    !Number.isFinite(resources.lives) ||
    !Number.isFinite(resources.maximumLives) ||
    resources.lives < 1 ||
    resources.maximumLives < 1
  ) {
    return 'last-life';
  }
  if (event.lifeDelta > 0 && resources.lives >= resources.maximumLives) {
    return 'maximum-lives';
  }
  if (event.lifeDelta < 0 && resources.lives + event.lifeDelta < 1) {
    return 'last-life';
  }
  if (event.activatesCurse && activeRunCurse) {
    return 'curse-slot-occupied';
  }
  if (event.id === 'echo-forge') {
    const upgradeId = event.duplicateUpgradeId;
    const maximumStacks = event.duplicateUpgradeMaximumStacks;
    if (
      !upgradeId ||
      maximumStacks === null ||
      (upgradeStacks[upgradeId] ?? 0) < 1 ||
      (upgradeStacks[upgradeId] ?? 0) >= maximumStacks
    ) {
      return 'upgrade-unavailable';
    }
  } else if (event.duplicateUpgradeId !== null || event.duplicateUpgradeMaximumStacks !== null) {
    return 'upgrade-unavailable';
  }
  if (
    event.id === 'glass-overclock'
      ? !event.activatesCurse ||
        event.activatesCurse.id !== 'glass-overclock' ||
        event.activatesCurse.source !== 'event' ||
        event.activatesCurse.sourceRelicId !== null ||
        !Number.isInteger(event.activatesCurse.startWave) ||
        !Number.isInteger(event.activatesCurse.endWave) ||
        event.activatesCurse.startWave < 1 ||
        event.activatesCurse.endWave < event.activatesCurse.startWave ||
        event.activatesCurse.endWave - event.activatesCurse.startWave + 1 >
          RUN_EVENT_CURSE_SAFETY_LIMITS.maximumDurationWaves ||
        !Number.isFinite(event.activatesCurse.damageMultiplier) ||
        event.activatesCurse.damageMultiplier < 1 ||
        event.activatesCurse.damageMultiplier >
          RUN_EVENT_CURSE_SAFETY_LIMITS.maximumDamageMultiplier ||
        !Number.isFinite(event.activatesCurse.paddleWidthMultiplier) ||
        event.activatesCurse.paddleWidthMultiplier <
          RUN_EVENT_CURSE_SAFETY_LIMITS.minimumPaddleWidthMultiplier ||
        event.activatesCurse.paddleWidthMultiplier > 1 ||
        protectedRunCurseWaves.some(
          (wave) =>
            Number.isInteger(wave) &&
            wave >= event.activatesCurse!.startWave &&
            wave <= event.activatesCurse!.endWave,
        )
      : event.activatesCurse !== null
  ) {
    return 'unsafe-curse-combination';
  }
  return 'available';
}

export class RunProgression {
  readonly #seed: string;
  readonly #maxWaves: number;
  readonly #createUpgradeChoices: RunProgressionOptions['createUpgradeChoices'];
  readonly #bossRewardWaves: ReadonlySet<number>;
  readonly #createRelicChoices: NonNullable<RunProgressionOptions['createRelicChoices']>;
  readonly #createRelicCurse: NonNullable<RunProgressionOptions['createRelicCurse']>;
  readonly #createRiskContractOffer: NonNullable<RunProgressionOptions['createRiskContractOffer']>;
  readonly #createRunEventOffer: NonNullable<RunProgressionOptions['createRunEventOffer']>;
  readonly #createRunCleanseOffer: NonNullable<RunProgressionOptions['createRunCleanseOffer']>;
  readonly #protectedRunCurseWaves: readonly number[];
  readonly #upgradeStacks: Partial<Record<UpgradeId, number>> = {};
  #wave: number;
  #phase: RunPhase = 'combat';
  #rewardKind: RunRewardKind = 'upgrade';
  #upgradeChoices: UpgradeId[] = [];
  #relicChoices: RelicId[] = [];
  #activeRelicIds: RelicId[] = [];
  #rewardRerollAvailable = false;
  #rewardRerollIndex = 0;
  #pendingRiskContract: RiskContractSnapshot | null = null;
  #activeRiskContract: RiskContractSnapshot | null = null;
  #pendingRunEvent: RunEventSnapshot | null = null;
  #pendingRunCleanse: RunCleanseSnapshot | null = null;
  #activeRunCurse: RunCurseSnapshot | null = null;
  #elapsedMilliseconds = 0;
  #stats = createInitialRunStats();

  constructor(options: RunProgressionOptions) {
    this.#seed = options.seed;
    this.#maxWaves = Math.max(1, Math.floor(options.maxWaves));
    this.#wave = clamp(Math.floor(options.startingWave ?? 1), 1, this.#maxWaves);
    this.#createUpgradeChoices = options.createUpgradeChoices;
    const configuredBossRewardWaves = options.bossRewardWaves ?? [options.bossRewardWave ?? 10];
    this.#bossRewardWaves = new Set(
      configuredBossRewardWaves.map((wave) => clamp(Math.floor(wave), 1, this.#maxWaves)),
    );
    this.#createRelicChoices = options.createRelicChoices ?? (() => []);
    this.#createRelicCurse = options.createRelicCurse ?? (() => null);
    this.#createRiskContractOffer = options.createRiskContractOffer ?? (() => null);
    this.#createRunEventOffer = options.createRunEventOffer ?? (() => null);
    this.#createRunCleanseOffer = options.createRunCleanseOffer ?? (() => null);
    this.#protectedRunCurseWaves = [
      ...new Set(
        (options.protectedRunCurseWaves ?? [])
          .filter((wave) => Number.isFinite(wave))
          .map((wave) => Math.floor(wave)),
      ),
    ];
    for (const upgradeId of options.initialUpgradeIds) {
      this.#upgradeStacks[upgradeId] = (this.#upgradeStacks[upgradeId] ?? 0) + 1;
    }
  }

  get snapshot(): RunProgressionSnapshot {
    return {
      seed: this.#seed,
      wave: this.#wave,
      maxWaves: this.#maxWaves,
      phase: this.#phase,
      rewardKind: this.#rewardKind,
      upgradeChoices: [...this.#upgradeChoices],
      relicChoices: [...this.#relicChoices],
      activeRelicIds: [...this.#activeRelicIds],
      rewardRerollAvailable: this.#rewardRerollAvailable,
      upgradeStacks: { ...this.#upgradeStacks },
      pendingRiskContract: this.#pendingRiskContract ? { ...this.#pendingRiskContract } : null,
      activeRiskContract: this.#activeRiskContract ? { ...this.#activeRiskContract } : null,
      pendingRunEvent: cloneRunEvent(this.#pendingRunEvent),
      pendingRunCleanse: cloneRunCleanse(this.#pendingRunCleanse),
      activeRunCurse: cloneRunCurse(this.#activeRunCurse),
      stats: { ...this.#stats },
    };
  }

  completeWave(): boolean {
    if (this.#phase !== 'combat') {
      return false;
    }

    this.#stats = { ...this.#stats, wavesCleared: this.#stats.wavesCleared + 1 };
    if (this.#activeRunCurse && this.#activeRunCurse.endWave <= this.#wave) {
      this.#activeRunCurse = null;
    }
    if (this.#wave >= this.#maxWaves) {
      this.#phase = 'won';
      this.#upgradeChoices = [];
      this.#relicChoices = [];
      this.#rewardRerollAvailable = false;
      this.#pendingRiskContract = null;
      this.#pendingRunEvent = null;
      this.#pendingRunCleanse = null;
      this.#activeRunCurse = null;
      return true;
    }

    this.#phase = 'choosing-upgrade';
    this.#rewardRerollAvailable = true;
    this.#rewardRerollIndex = 0;
    if (this.#bossRewardWaves.has(this.#wave)) {
      this.#rewardKind = 'relic';
      this.#upgradeChoices = [];
      this.#relicChoices = [
        ...this.#createRelicChoices(
          this.#wave,
          [...this.#activeRelicIds],
          0,
          cloneRunCurse(this.#activeRunCurse),
        ),
      ];
    } else {
      this.#rewardKind = 'upgrade';
      this.#relicChoices = [];
      this.#upgradeChoices = [
        ...this.#createUpgradeChoices(this.#wave, { ...this.#upgradeStacks }, 0),
      ];
    }
    if (this.#upgradeChoices.length === 0 && this.#relicChoices.length === 0) {
      this.#stats = {
        ...this.#stats,
        rewardsSkipped: this.#stats.rewardsSkipped + 1,
        rewardsExhausted: this.#stats.rewardsExhausted + 1,
      };
      this.#advanceAfterReward();
    }
    return true;
  }

  chooseUpgrade(upgradeId: UpgradeId): boolean {
    if (
      this.#phase !== 'choosing-upgrade' ||
      this.#rewardKind !== 'upgrade' ||
      !this.#upgradeChoices.includes(upgradeId)
    ) {
      return false;
    }

    this.#upgradeStacks[upgradeId] = (this.#upgradeStacks[upgradeId] ?? 0) + 1;
    this.#stats = { ...this.#stats, upgradesChosen: this.#stats.upgradesChosen + 1 };
    this.#advanceAfterReward();
    return true;
  }

  chooseRelic(relicId: RelicId): boolean {
    if (
      this.#phase !== 'choosing-upgrade' ||
      this.#rewardKind !== 'relic' ||
      !this.#relicChoices.includes(relicId) ||
      this.#activeRelicIds.includes(relicId)
    ) {
      return false;
    }

    const attachedCurse = this.#createRelicCurse(relicId, this.#wave);
    if (
      getRunRelicAvailability(
        relicId,
        attachedCurse,
        this.#activeRunCurse,
        this.#protectedRunCurseWaves,
      ) !== 'available' ||
      (attachedCurse !== null && attachedCurse.startWave !== this.#wave + 1)
    ) {
      return false;
    }

    this.#activeRelicIds.push(relicId);
    if (attachedCurse) {
      this.#activeRunCurse = cloneRunCurse(attachedCurse);
    }
    this.#stats = { ...this.#stats, relicsChosen: this.#stats.relicsChosen + 1 };
    this.#advanceAfterReward();
    return true;
  }

  rerollReward(): boolean {
    if (this.#phase !== 'choosing-upgrade' || !this.#rewardRerollAvailable) {
      return false;
    }
    const hasChoices =
      this.#rewardKind === 'relic'
        ? this.#relicChoices.length > 0
        : this.#upgradeChoices.length > 0;
    if (!hasChoices) {
      return false;
    }

    const nextRerollIndex = this.#rewardRerollIndex + 1;
    if (this.#rewardKind === 'relic') {
      const nextChoices = [
        ...this.#createRelicChoices(
          this.#wave,
          [...this.#activeRelicIds],
          nextRerollIndex,
          cloneRunCurse(this.#activeRunCurse),
        ),
      ];
      if (nextChoices.length === 0) {
        return false;
      }
      this.#relicChoices = nextChoices;
    } else {
      const nextChoices = [
        ...this.#createUpgradeChoices(this.#wave, { ...this.#upgradeStacks }, nextRerollIndex),
      ];
      if (nextChoices.length === 0) {
        return false;
      }
      this.#upgradeChoices = nextChoices;
    }
    this.#rewardRerollAvailable = false;
    this.#rewardRerollIndex = nextRerollIndex;
    this.#stats = { ...this.#stats, rerollsUsed: this.#stats.rerollsUsed + 1 };
    return true;
  }

  skipReward(): boolean {
    if (this.#phase !== 'choosing-upgrade') {
      return false;
    }
    this.#stats = { ...this.#stats, rewardsSkipped: this.#stats.rewardsSkipped + 1 };
    this.#advanceAfterReward();
    return true;
  }

  continueWithoutUpgrade(): boolean {
    if (
      this.#phase !== 'choosing-upgrade' ||
      this.#upgradeChoices.length > 0 ||
      this.#relicChoices.length > 0
    ) {
      return false;
    }
    return this.skipReward();
  }

  resolveRiskContract(accepted: boolean): boolean {
    if (this.#phase !== 'choosing-contract' || !this.#pendingRiskContract) {
      return false;
    }

    this.#activeRiskContract = accepted ? this.#pendingRiskContract : null;
    this.#pendingRiskContract = null;
    this.#advanceAfterContract();
    return true;
  }

  resolveRunEvent(accepted: boolean, resources: RunEventResources): RunEventResolution | null {
    const event = this.#pendingRunEvent;
    if (this.#phase !== 'choosing-event' || !event) {
      return null;
    }

    let score = resources.score;
    let lives = resources.lives;
    let duplicatedUpgradeId: UpgradeId | null = null;
    let activatedCurse: RunCurseSnapshot | null = null;
    if (accepted) {
      if (
        getRunEventAvailability(
          event,
          resources,
          this.#upgradeStacks,
          this.#activeRunCurse,
          this.#protectedRunCurseWaves,
        ) !== 'available' ||
        (event.activatesCurse !== null &&
          (event.activatesCurse.startWave !== this.#wave + 1 ||
            event.activatesCurse.endWave < event.activatesCurse.startWave ||
            event.activatesCurse.endWave >
              event.activatesCurse.startWave +
                RUN_EVENT_CURSE_SAFETY_LIMITS.maximumDurationWaves -
                1))
      ) {
        return null;
      }
      score += event.scoreDelta;
      lives += event.lifeDelta;
      if (event.duplicateUpgradeId) {
        duplicatedUpgradeId = event.duplicateUpgradeId;
        this.#upgradeStacks[duplicatedUpgradeId] =
          (this.#upgradeStacks[duplicatedUpgradeId] ?? 0) + 1;
      }
      if (event.activatesCurse) {
        this.#activeRunCurse = cloneRunCurse(event.activatesCurse);
        activatedCurse = cloneRunCurse(event.activatesCurse);
      }
    }

    this.#pendingRunEvent = null;
    this.#advanceAfterEvent();
    return {
      eventId: event.id,
      accepted,
      score,
      lives,
      duplicatedUpgradeId,
      activatedCurse,
    };
  }

  resolveRunCleanse(cleansed: boolean): RunCleanseResolution | null {
    const cleanse = this.#pendingRunCleanse;
    const activeCurse = this.#activeRunCurse;
    if (
      this.#phase !== 'choosing-cleanse' ||
      !cleanse ||
      !isValidRunCleanseOffer(cleanse, activeCurse, this.#activeRelicIds, this.#wave)
    ) {
      return null;
    }

    const cleansedCurse = cleansed ? cloneRunCurse(activeCurse) : null;
    const removedRelicId = cleansed ? cleanse.removesRelicId : null;
    if (cleansed) {
      this.#activeRunCurse = null;
      if (removedRelicId) {
        this.#activeRelicIds = this.#activeRelicIds.filter((relicId) => relicId !== removedRelicId);
      }
    }
    this.#pendingRunCleanse = null;
    this.#advanceToNextWave();
    return {
      cleanseId: cleanse.id,
      cleansed,
      cleansedCurse,
      removedRelicId,
    };
  }

  lose(): boolean {
    if (this.#phase === 'won' || this.#phase === 'lost') {
      return false;
    }
    this.#phase = 'lost';
    this.#upgradeChoices = [];
    this.#relicChoices = [];
    this.#rewardRerollAvailable = false;
    this.#pendingRiskContract = null;
    this.#pendingRunEvent = null;
    this.#pendingRunCleanse = null;
    this.#activeRunCurse = null;
    return true;
  }

  recordBrickDestroyed(count = 1): void {
    if (this.#phase !== 'combat') {
      return;
    }
    this.#stats = {
      ...this.#stats,
      bricksDestroyed: this.#stats.bricksDestroyed + Math.max(0, Math.floor(count)),
    };
  }

  recordLifeLost(): void {
    if (this.#phase !== 'combat') {
      return;
    }
    this.#stats = { ...this.#stats, livesLost: this.#stats.livesLost + 1 };
  }

  recordProjectileCount(activeCount: number): void {
    const peakProjectiles = Math.max(this.#stats.peakProjectiles, Math.floor(activeCount));
    if (peakProjectiles !== this.#stats.peakProjectiles) {
      this.#stats = { ...this.#stats, peakProjectiles };
    }
  }

  advanceElapsedMilliseconds(deltaMilliseconds: number): boolean {
    if (this.#phase !== 'combat') {
      return false;
    }
    const previousElapsedSeconds = this.#stats.elapsedSeconds;
    this.#elapsedMilliseconds += Math.max(0, deltaMilliseconds);
    const elapsedSeconds = Math.floor(this.#elapsedMilliseconds / 1000);
    if (elapsedSeconds === previousElapsedSeconds) {
      return false;
    }
    this.#stats = { ...this.#stats, elapsedSeconds };
    return true;
  }

  #advanceAfterReward(): void {
    this.#upgradeChoices = [];
    this.#relicChoices = [];
    this.#rewardRerollAvailable = false;
    if (this.#activeRiskContract && this.#activeRiskContract.endWave <= this.#wave) {
      this.#activeRiskContract = null;
    }
    const riskContractOffer = this.#createRiskContractOffer(this.#wave);
    if (riskContractOffer) {
      this.#pendingRiskContract = { ...riskContractOffer };
      this.#phase = 'choosing-contract';
      return;
    }
    this.#advanceAfterContract();
  }

  #advanceAfterContract(): void {
    const runEventOffer = this.#createRunEventOffer(this.#wave, { ...this.#upgradeStacks });
    if (runEventOffer) {
      this.#pendingRunEvent = cloneRunEvent(runEventOffer);
      this.#phase = 'choosing-event';
      return;
    }
    this.#advanceAfterEvent();
  }

  #advanceAfterEvent(): void {
    const runCleanseOffer = this.#createRunCleanseOffer(
      this.#wave,
      cloneRunCurse(this.#activeRunCurse),
    );
    if (
      runCleanseOffer &&
      isValidRunCleanseOffer(
        runCleanseOffer,
        this.#activeRunCurse,
        this.#activeRelicIds,
        this.#wave,
      )
    ) {
      this.#pendingRunCleanse = cloneRunCleanse(runCleanseOffer);
      this.#phase = 'choosing-cleanse';
      return;
    }
    this.#advanceToNextWave();
  }

  #advanceToNextWave(): void {
    this.#wave = Math.min(this.#maxWaves, this.#wave + 1);
    this.#phase = 'combat';
  }
}

interface BossStateMachineOptions {
  readonly maxHitPoints: number;
  readonly phaseThresholds: readonly [number, number];
}

export interface BossDamageResult {
  readonly damageApplied: number;
  readonly enteredPhases: readonly BossPhaseId[];
  readonly defeated: boolean;
}

export class BossStateMachine {
  readonly #maxHitPoints: number;
  readonly #phaseThresholds: readonly [number, number];
  #hitPoints: number;
  #phase: BossPhaseId = 1;

  constructor(options: BossStateMachineOptions) {
    this.#maxHitPoints = Math.max(1, options.maxHitPoints);
    this.#hitPoints = this.#maxHitPoints;
    const firstThreshold = clamp(options.phaseThresholds[0], 0, 1);
    const secondThreshold = clamp(options.phaseThresholds[1], 0, firstThreshold);
    this.#phaseThresholds = [firstThreshold, secondThreshold];
  }

  get snapshot(): BossSnapshot {
    return {
      active: this.#hitPoints > 0,
      phase: this.#phase,
      hitPoints: this.#hitPoints,
      maxHitPoints: this.#maxHitPoints,
      healthRatio: this.#hitPoints / this.#maxHitPoints,
    };
  }

  damage(requestedDamage: number): BossDamageResult {
    if (this.#hitPoints <= 0) {
      return { damageApplied: 0, enteredPhases: [], defeated: true };
    }

    const damageApplied = Math.min(this.#hitPoints, Math.max(0, requestedDamage));
    this.#hitPoints -= damageApplied;
    const nextPhase = this.#calculatePhase();
    const enteredPhases: BossPhaseId[] = [];
    for (let phase = this.#phase + 1; phase <= nextPhase; phase += 1) {
      enteredPhases.push(phase as BossPhaseId);
    }
    this.#phase = nextPhase;
    return { damageApplied, enteredPhases, defeated: this.#hitPoints <= 0 };
  }

  #calculatePhase(): BossPhaseId {
    const healthRatio = this.#hitPoints / this.#maxHitPoints;
    if (healthRatio <= this.#phaseThresholds[1]) {
      return 3;
    }
    if (healthRatio <= this.#phaseThresholds[0]) {
      return 2;
    }
    return 1;
  }
}

export interface BossShieldDamageResult {
  readonly damageApplied: number;
  readonly nodeDestroyed: boolean;
  readonly shieldBroken: boolean;
}

export interface BossShieldAdvanceResult {
  readonly restored: boolean;
}

export class BossShieldStateMachine {
  readonly #nodeCount: number;
  readonly #maxNodeHitPoints: number;
  readonly #vulnerableDurationMilliseconds: number;
  #nodeHitPoints: number[];
  #vulnerableRemainingMilliseconds = 0;
  #cycle = 1;

  constructor(
    options: Readonly<{
      nodeCount: number;
      nodeHitPoints: number;
      vulnerableDurationMilliseconds: number;
    }>,
  ) {
    this.#nodeCount = Math.max(
      1,
      Math.floor(Number.isFinite(options.nodeCount) ? options.nodeCount : 1),
    );
    this.#maxNodeHitPoints = Math.max(
      1,
      Number.isFinite(options.nodeHitPoints) ? options.nodeHitPoints : 1,
    );
    this.#vulnerableDurationMilliseconds = Math.max(
      1,
      Number.isFinite(options.vulnerableDurationMilliseconds)
        ? options.vulnerableDurationMilliseconds
        : 1,
    );
    this.#nodeHitPoints = Array.from({ length: this.#nodeCount }, () => this.#maxNodeHitPoints);
  }

  get snapshot(): BossShieldSnapshot {
    const nodesRemaining = this.#nodeHitPoints.filter((hitPoints) => hitPoints > 0).length;
    return {
      active: nodesRemaining > 0,
      cycle: this.#cycle,
      nodesRemaining,
      nodeHitPoints: [...this.#nodeHitPoints],
      maxNodeHitPoints: this.#maxNodeHitPoints,
      vulnerableRemainingMilliseconds: this.#vulnerableRemainingMilliseconds,
      vulnerableDurationMilliseconds: this.#vulnerableDurationMilliseconds,
    };
  }

  damageNode(nodeIndex: number, requestedDamage: number): BossShieldDamageResult {
    const safeIndex = Math.floor(nodeIndex);
    const hitPoints = this.#nodeHitPoints[safeIndex];
    const damage = Number.isFinite(requestedDamage) ? Math.max(0, requestedDamage) : 0;
    if (!Number.isInteger(nodeIndex) || hitPoints === undefined || hitPoints <= 0 || damage <= 0) {
      return { damageApplied: 0, nodeDestroyed: false, shieldBroken: false };
    }

    const damageApplied = Math.min(hitPoints, damage);
    this.#nodeHitPoints[safeIndex] = hitPoints - damageApplied;
    const nodeDestroyed = this.#nodeHitPoints[safeIndex] === 0;
    const shieldBroken = nodeDestroyed && this.#nodeHitPoints.every((value) => value <= 0);
    if (shieldBroken) {
      this.#vulnerableRemainingMilliseconds = this.#vulnerableDurationMilliseconds;
    }
    return { damageApplied, nodeDestroyed, shieldBroken };
  }

  advance(deltaMilliseconds: number): BossShieldAdvanceResult {
    const delta = Number.isFinite(deltaMilliseconds) ? Math.max(0, deltaMilliseconds) : 0;
    if (this.snapshot.active || delta <= 0) {
      return { restored: false };
    }
    this.#vulnerableRemainingMilliseconds = Math.max(
      0,
      this.#vulnerableRemainingMilliseconds - delta,
    );
    if (this.#vulnerableRemainingMilliseconds > 0) {
      return { restored: false };
    }
    this.#cycle += 1;
    this.#nodeHitPoints = Array.from({ length: this.#nodeCount }, () => this.#maxNodeHitPoints);
    return { restored: true };
  }
}

export interface BossSummonAdvanceResult {
  readonly summonedSequences: readonly number[];
}

export class BossSummonStateMachine {
  readonly #capacity: number;
  readonly #summonIntervalsByPhase: readonly [number, number, number];
  readonly #bossDamageReductionPerReinforcement: number;
  readonly #minimumBossDamageMultiplier: number;
  #phase: BossPhaseId = 1;
  #reinforcementsActive = 0;
  #summonSequence = 0;
  #summonRemainingMilliseconds: number;

  constructor(
    options: Readonly<{
      capacity: number;
      initialDelayMilliseconds: number;
      summonIntervalsByPhase: readonly [number, number, number];
      bossDamageReductionPerReinforcement: number;
      minimumBossDamageMultiplier: number;
    }>,
  ) {
    this.#capacity = Math.max(
      1,
      Math.floor(Number.isFinite(options.capacity) ? options.capacity : 1),
    );
    this.#summonIntervalsByPhase = options.summonIntervalsByPhase.map((interval) =>
      Math.max(1, Number.isFinite(interval) ? interval : 1),
    ) as unknown as readonly [number, number, number];
    this.#bossDamageReductionPerReinforcement = clamp(
      Number.isFinite(options.bossDamageReductionPerReinforcement)
        ? options.bossDamageReductionPerReinforcement
        : 0,
      0,
      1,
    );
    this.#minimumBossDamageMultiplier = clamp(
      Number.isFinite(options.minimumBossDamageMultiplier)
        ? options.minimumBossDamageMultiplier
        : 0,
      0,
      1,
    );
    this.#summonRemainingMilliseconds = Math.max(
      1,
      Number.isFinite(options.initialDelayMilliseconds) ? options.initialDelayMilliseconds : 1,
    );
  }

  get snapshot(): BossSummonSnapshot {
    return {
      phase: this.#phase,
      reinforcementsActive: this.#reinforcementsActive,
      reinforcementCapacity: this.#capacity,
      summonSequence: this.#summonSequence,
      summonRemainingMilliseconds: this.#summonRemainingMilliseconds,
      summonIntervalMilliseconds: this.#currentInterval,
      bossDamageMultiplier:
        Math.round(
          Math.max(
            this.#minimumBossDamageMultiplier,
            1 - this.#reinforcementsActive * this.#bossDamageReductionPerReinforcement,
          ) * 10_000,
        ) / 10_000,
    };
  }

  advance(deltaMilliseconds: number): BossSummonAdvanceResult {
    const summonedSequences: number[] = [];
    let remainingDelta = Number.isFinite(deltaMilliseconds) ? Math.max(0, deltaMilliseconds) : 0;
    if (remainingDelta <= 0 || this.#reinforcementsActive >= this.#capacity) {
      return { summonedSequences };
    }

    while (
      remainingDelta >= this.#summonRemainingMilliseconds &&
      this.#reinforcementsActive < this.#capacity
    ) {
      remainingDelta -= this.#summonRemainingMilliseconds;
      summonedSequences.push(this.#summonOne());
      this.#summonRemainingMilliseconds = this.#currentInterval;
    }
    if (this.#reinforcementsActive < this.#capacity) {
      this.#summonRemainingMilliseconds = Math.max(
        0,
        this.#summonRemainingMilliseconds - remainingDelta,
      );
    } else {
      this.#summonRemainingMilliseconds = this.#currentInterval;
    }
    return { summonedSequences };
  }

  enterPhase(phase: BossPhaseId): BossSummonAdvanceResult {
    if (phase <= this.#phase) {
      return { summonedSequences: [] };
    }
    this.#phase = phase;
    this.#summonRemainingMilliseconds = this.#currentInterval;
    return this.#reinforcementsActive < this.#capacity
      ? { summonedSequences: [this.#summonOne()] }
      : { summonedSequences: [] };
  }

  recordReinforcementDestroyed(): boolean {
    if (this.#reinforcementsActive <= 0) {
      return false;
    }
    this.#reinforcementsActive -= 1;
    return true;
  }

  get #currentInterval(): number {
    return this.#summonIntervalsByPhase[this.#phase - 1]!;
  }

  #summonOne(): number {
    this.#reinforcementsActive += 1;
    this.#summonSequence += 1;
    return this.#summonSequence;
  }
}

export interface BossSingularityTransition {
  readonly action: 'field-warned' | 'field-activated' | 'field-ended';
  readonly cycle: number;
  readonly pullSide: BossSingularityPullSide;
}

export interface BossSingularityAdvanceResult {
  readonly transitions: readonly BossSingularityTransition[];
}

export class BossSingularityStateMachine {
  readonly #warningDurationMilliseconds: number;
  readonly #activeDurationMilliseconds: number;
  readonly #cooldownMillisecondsByPhase: readonly [number, number, number];
  readonly #steeringRadiansPerSecondByPhase: readonly [number, number, number];
  readonly #aegisSteeringMultiplier: number;
  #phase: BossPhaseId = 1;
  #stage: BossSingularityStage = 'warning';
  #cycle = 1;
  #remainingMilliseconds: number;

  constructor(
    options: Readonly<{
      warningDurationMilliseconds: number;
      activeDurationMilliseconds: number;
      cooldownMillisecondsByPhase: readonly [number, number, number];
      steeringRadiansPerSecondByPhase: readonly [number, number, number];
      aegisSteeringMultiplier: number;
    }>,
  ) {
    this.#warningDurationMilliseconds = positiveFinite(options.warningDurationMilliseconds);
    this.#activeDurationMilliseconds = positiveFinite(options.activeDurationMilliseconds);
    this.#cooldownMillisecondsByPhase = options.cooldownMillisecondsByPhase.map(
      positiveFinite,
    ) as unknown as readonly [number, number, number];
    this.#steeringRadiansPerSecondByPhase = options.steeringRadiansPerSecondByPhase.map((value) =>
      Math.max(0, Number.isFinite(value) ? value : 0),
    ) as unknown as readonly [number, number, number];
    this.#aegisSteeringMultiplier = clamp(
      Number.isFinite(options.aegisSteeringMultiplier) ? options.aegisSteeringMultiplier : 0,
      0,
      1,
    );
    this.#remainingMilliseconds = this.#warningDurationMilliseconds;
  }

  get snapshot(): BossSingularitySnapshot {
    return {
      phase: this.#phase,
      stage: this.#stage,
      cycle: this.#cycle,
      pullSide: this.#pullSide,
      remainingMilliseconds: this.#remainingMilliseconds,
      stageDurationMilliseconds: this.#stageDurationMilliseconds,
      steeringRadiansPerSecond: this.#steeringRadiansPerSecondByPhase[this.#phase - 1]!,
      aegisSteeringMultiplier: this.#aegisSteeringMultiplier,
    };
  }

  advance(deltaMilliseconds: number): BossSingularityAdvanceResult {
    const transitions: BossSingularityTransition[] = [];
    let delta = Number.isFinite(deltaMilliseconds)
      ? Math.min(Math.max(0, deltaMilliseconds), 60_000)
      : 0;
    while (delta >= this.#remainingMilliseconds) {
      delta -= this.#remainingMilliseconds;
      if (this.#stage === 'warning') {
        this.#stage = 'active';
        transitions.push({
          action: 'field-activated',
          cycle: this.#cycle,
          pullSide: this.#pullSide,
        });
      } else if (this.#stage === 'active') {
        transitions.push({
          action: 'field-ended',
          cycle: this.#cycle,
          pullSide: this.#pullSide,
        });
        this.#cycle += 1;
        this.#stage = 'cooldown';
      } else {
        this.#stage = 'warning';
        transitions.push({
          action: 'field-warned',
          cycle: this.#cycle,
          pullSide: this.#pullSide,
        });
      }
      this.#remainingMilliseconds = this.#stageDurationMilliseconds;
    }
    this.#remainingMilliseconds -= delta;
    return { transitions };
  }

  enterPhase(phase: BossPhaseId): void {
    if (phase <= this.#phase) {
      return;
    }
    this.#phase = phase;
    if (this.#stage === 'cooldown') {
      this.#remainingMilliseconds = Math.min(
        this.#remainingMilliseconds,
        this.#stageDurationMilliseconds,
      );
    }
  }

  get #pullSide(): BossSingularityPullSide {
    return this.#cycle % 2 === 1 ? 'left' : 'right';
  }

  get #stageDurationMilliseconds(): number {
    return this.#stage === 'warning'
      ? this.#warningDurationMilliseconds
      : this.#stage === 'active'
        ? this.#activeDurationMilliseconds
        : this.#cooldownMillisecondsByPhase[this.#phase - 1]!;
  }
}

export class ActiveAbilityController {
  readonly #cooldowns: Record<ActiveAbilityId, number>;
  readonly #remaining: Record<ActiveAbilityId, number>;

  constructor(cooldowns: Readonly<Record<ActiveAbilityId, number>>) {
    this.#cooldowns = Object.fromEntries(
      Object.entries(cooldowns).map(([abilityId, cooldown]) => [abilityId, Math.max(0, cooldown)]),
    ) as Record<ActiveAbilityId, number>;
    this.#remaining = Object.fromEntries(
      Object.keys(cooldowns).map((abilityId) => [abilityId, 0]),
    ) as Record<ActiveAbilityId, number>;
  }

  get snapshot(): ActiveAbilityStates {
    return Object.fromEntries(
      (Object.keys(this.#cooldowns) as ActiveAbilityId[]).map((abilityId) => [
        abilityId,
        this.#createState(abilityId),
      ]),
    ) as ActiveAbilityStates;
  }

  use(abilityId: ActiveAbilityId): boolean {
    if (this.#remaining[abilityId] > 0) {
      return false;
    }
    this.#remaining[abilityId] = this.#cooldowns[abilityId];
    return true;
  }

  advance(deltaMilliseconds: number): void {
    const delta = Math.max(0, deltaMilliseconds);
    for (const abilityId of Object.keys(this.#remaining) as ActiveAbilityId[]) {
      this.#remaining[abilityId] = Math.max(0, this.#remaining[abilityId] - delta);
    }
  }

  reset(): void {
    for (const abilityId of Object.keys(this.#remaining) as ActiveAbilityId[]) {
      this.#remaining[abilityId] = 0;
    }
  }

  #createState(abilityId: ActiveAbilityId): ActiveAbilityState {
    const remainingMilliseconds = this.#remaining[abilityId];
    return {
      ready: remainingMilliseconds <= 0,
      remainingMilliseconds,
      cooldownMilliseconds: this.#cooldowns[abilityId],
    };
  }
}

export type CombatEffectHandler<TContext> = (
  trigger: CombatEffectTrigger,
  context: TContext,
) => void;

export class CombatEffectRegistry<TContext> {
  readonly #handlers = new Map<CombatEffectId, CombatEffectHandler<TContext>>();

  register(effectId: CombatEffectId, handler: CombatEffectHandler<TContext>): this {
    if (this.#handlers.has(effectId)) {
      throw new Error(`Effect already registered: ${effectId}`);
    }

    this.#handlers.set(effectId, handler);
    return this;
  }

  execute(trigger: CombatEffectTrigger, context: TContext): boolean {
    const handler = this.#handlers.get(trigger.effectId);
    if (!handler) {
      return false;
    }

    handler(trigger, context);
    return true;
  }
}

export class TypedCombatEventBus<TEvent extends { readonly type: string }> {
  readonly #listeners = new Map<TEvent['type'], Set<(event: TEvent) => void>>();

  on<TType extends TEvent['type']>(
    type: TType,
    listener: (event: Extract<TEvent, { readonly type: TType }>) => void,
  ): () => void {
    const listeners = this.#listeners.get(type) ?? new Set<(event: TEvent) => void>();
    const wrappedListener = (event: TEvent) => {
      listener(event as Extract<TEvent, { readonly type: TType }>);
    };
    listeners.add(wrappedListener);
    this.#listeners.set(type, listeners);
    return () => listeners.delete(wrappedListener);
  }

  emit(event: TEvent): void {
    const listeners = this.#listeners.get(event.type);
    if (!listeners) {
      return;
    }
    for (const listener of listeners) {
      listener(event);
    }
  }

  clear(): void {
    this.#listeners.clear();
  }
}

export class FixedObjectPool<T extends object> {
  readonly #allItems: Set<T>;
  readonly #availableItems: T[];
  readonly #availableSet: Set<T>;

  constructor(items: readonly T[]) {
    this.#allItems = new Set(items);
    this.#availableItems = [...this.#allItems];
    this.#availableSet = new Set(this.#allItems);
  }

  get capacity(): number {
    return this.#allItems.size;
  }

  get activeCount(): number {
    return this.capacity - this.#availableItems.length;
  }

  get availableCount(): number {
    return this.#availableItems.length;
  }

  acquire(): T | undefined {
    const item = this.#availableItems.pop();
    if (item !== undefined) {
      this.#availableSet.delete(item);
    }
    return item;
  }

  release(item: T): boolean {
    if (!this.#allItems.has(item) || this.#availableSet.has(item)) {
      return false;
    }

    this.#availableItems.push(item);
    this.#availableSet.add(item);
    return true;
  }

  isActive(item: T): boolean {
    return this.#allItems.has(item) && !this.#availableSet.has(item);
  }
}

export function createRadialBurstVelocities(input: RadialBurstInput): Velocity[] {
  const count = Math.max(0, Math.floor(input.count));
  if (count === 0) {
    return [];
  }

  const headingRadians = input.headingRadians ?? -Math.PI / 2;
  const angleStep = (Math.PI * 2) / count;
  return Array.from({ length: count }, (_, index) => {
    const angle = headingRadians + angleStep * index;
    return {
      x: Math.cos(angle) * input.speed,
      y: Math.sin(angle) * input.speed,
    };
  });
}

export function calculateAllowedProjectileSpawnCount(input: ProjectileSpawnLimitInput): number {
  const hardLimit = Math.max(0, Math.floor(input.hardLimit));
  const softLimit = clamp(Math.floor(input.softLimit), 0, hardLimit);
  const activeCount = clamp(Math.floor(input.activeCount), 0, hardLimit);
  const replacingCount = clamp(Math.floor(input.replacingCount), 0, activeCount);
  const retainedCount = activeCount - replacingCount;
  const limit = input.allowBeyondSoftLimit ? hardLimit : softLimit;
  return clamp(Math.floor(input.requestedCount), 0, Math.max(0, limit - retainedCount));
}

export function shouldConsumeLifeAfterProjectileDrain(input: ProjectileDrainInput): boolean {
  return !input.stressMode && input.activeProjectileCount <= 0 && input.bricksRemaining > 0;
}

export function calculateSplitDamage(
  parentDamage: number,
  attenuationMultiplier = 0.45,
  minimumDamage = 0.2,
): number {
  return Math.max(minimumDamage, parentDamage * attenuationMultiplier);
}

export function selectEffectsQuality(
  activeProjectileCount: number,
  averageFrameTimeMilliseconds: number,
): EffectsQuality {
  if (activeProjectileCount >= 48 || averageFrameTimeMilliseconds >= 25) {
    return 'minimal';
  }
  if (activeProjectileCount >= 24 || averageFrameTimeMilliseconds >= 18) {
    return 'reduced';
  }
  return 'full';
}

export function clampAimOffset(aimOffsetDegrees: number): number {
  return clamp(aimOffsetDegrees, -MAXIMUM_AIM_OFFSET_DEGREES, MAXIMUM_AIM_OFFSET_DEGREES);
}

export function calculateLaunchVelocity(aimOffsetDegrees: number, speed: number): Velocity {
  const angleRadians = degreesToRadians(clampAimOffset(aimOffsetDegrees));

  return {
    x: Math.sin(angleRadians) * speed,
    y: -Math.cos(angleRadians) * speed,
  };
}

export function stabilizeProjectileVelocity(input: ProjectileTrajectoryInput): Velocity {
  const speed = Math.max(0, input.speed);
  if (speed === 0) {
    return { x: 0, y: 0 };
  }

  const fallbackVerticalDirection = input.fallbackVerticalDirection === 1 ? 1 : -1;
  const fallbackHorizontalDirection = input.fallbackHorizontalDirection === -1 ? -1 : 1;
  const currentSpeed = Math.hypot(input.velocity.x, input.velocity.y);
  const minimumVerticalRatio = clamp(input.minimumVerticalRatio ?? 0.38, 0, 0.95);
  const maximumHorizontalRatio = Math.sqrt(Math.max(1 - minimumVerticalRatio ** 2, 0));
  const minimumHorizontalRatio = Math.min(
    clamp(input.minimumHorizontalRatio ?? 0, 0, 0.95),
    maximumHorizontalRatio,
  );
  const minimumVerticalSpeed = speed * minimumVerticalRatio;
  const minimumHorizontalSpeed = speed * minimumHorizontalRatio;
  const normalizedVelocity =
    Number.isFinite(currentSpeed) && currentSpeed >= 0.001
      ? {
          x: (input.velocity.x / currentSpeed) * speed,
          y: (input.velocity.y / currentSpeed) * speed,
        }
      : { x: 0, y: fallbackVerticalDirection * speed };

  if (
    Math.abs(normalizedVelocity.x) >= minimumHorizontalSpeed &&
    Math.abs(normalizedVelocity.y) >= minimumVerticalSpeed
  ) {
    return normalizedVelocity;
  }

  const verticalDirection = Math.sign(input.velocity.y) || fallbackVerticalDirection;
  const horizontalDirection = Math.sign(input.velocity.x) || fallbackHorizontalDirection;
  if (Math.abs(normalizedVelocity.x) < minimumHorizontalSpeed) {
    return {
      x: horizontalDirection * minimumHorizontalSpeed,
      y:
        verticalDirection *
        Math.sqrt(Math.max(speed * speed - minimumHorizontalSpeed * minimumHorizontalSpeed, 0)),
    };
  }

  return {
    x:
      horizontalDirection *
      Math.sqrt(Math.max(speed * speed - minimumVerticalSpeed * minimumVerticalSpeed, 0)),
    y: verticalDirection * minimumVerticalSpeed,
  };
}

export function steerProjectileTowardSingularity(input: SingularitySteeringInput): Velocity {
  const speed = Math.max(0, Number.isFinite(input.speed) ? input.speed : 0);
  if (speed === 0) {
    return { x: 0, y: 0 };
  }
  const verticalDirection = input.velocity.y > 0 ? 1 : -1;
  const horizontalDirection = input.pullSide === 'left' ? -1 : 1;
  const stabilized = stabilizeProjectileVelocity({
    ...input,
    minimumHorizontalRatio: 0,
    fallbackVerticalDirection: verticalDirection,
    fallbackHorizontalDirection: horizontalDirection,
  });
  const currentAngle = Math.atan2(stabilized.y, stabilized.x);
  const targetVerticalRatio = 0.64;
  const targetAngle = Math.atan2(
    verticalDirection * speed * targetVerticalRatio,
    horizontalDirection * speed * Math.sqrt(1 - targetVerticalRatio ** 2),
  );
  const maximumTurn =
    Math.max(
      0,
      Number.isFinite(input.steeringRadiansPerSecond) ? input.steeringRadiansPerSecond : 0,
    ) *
    Math.min(
      Math.max(0, Number.isFinite(input.deltaMilliseconds) ? input.deltaMilliseconds : 0) / 1_000,
      0.05,
    ) *
    clamp(
      input.steeringMultiplier === undefined
        ? 1
        : Number.isFinite(input.steeringMultiplier)
          ? input.steeringMultiplier
          : 0,
      0,
      1,
    );
  const angleDelta = Math.atan2(
    Math.sin(targetAngle - currentAngle),
    Math.cos(targetAngle - currentAngle),
  );
  const nextAngle = currentAngle + clamp(angleDelta, -maximumTurn, maximumTurn);
  return stabilizeProjectileVelocity({
    ...input,
    velocity: { x: Math.cos(nextAngle) * speed, y: Math.sin(nextAngle) * speed },
    minimumHorizontalRatio: 0,
    fallbackVerticalDirection: verticalDirection,
    fallbackHorizontalDirection: horizontalDirection,
  });
}

export function calculatePaddleBounceVelocity(input: PaddleBounceInput): Velocity {
  const maximumAngleDegrees = input.maximumAngleDegrees ?? 62;
  const minimumVerticalSpeed = input.minimumVerticalSpeed ?? input.speed * 0.42;
  const halfPaddleWidth = Math.max(input.paddleWidth / 2, 1);
  const hitOffset = clamp((input.ballX - input.paddleX) / halfPaddleWidth, -1, 1);
  const bounceAngleRadians = degreesToRadians(hitOffset * maximumAngleDegrees);
  const baseHorizontalSpeed = Math.sin(bounceAngleRadians) * input.speed;
  const movementInfluence = clamp(
    input.paddleVelocityX * 0.18,
    -input.speed * 0.22,
    input.speed * 0.22,
  );
  let horizontalSpeed = clamp(
    baseHorizontalSpeed + movementInfluence,
    -input.speed * 0.9,
    input.speed * 0.9,
  );
  let verticalSpeed = -Math.sqrt(
    Math.max(input.speed * input.speed - horizontalSpeed * horizontalSpeed, 0),
  );

  if (Math.abs(verticalSpeed) < minimumVerticalSpeed) {
    verticalSpeed = -minimumVerticalSpeed;
    const correctedHorizontalSpeed = Math.sqrt(
      Math.max(input.speed * input.speed - verticalSpeed * verticalSpeed, 0),
    );
    horizontalSpeed =
      Math.sign(horizontalSpeed || hitOffset || input.paddleVelocityX) * correctedHorizontalSpeed;
  }

  return {
    x: horizontalSpeed,
    y: verticalSpeed,
  };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function positiveFinite(value: number): number {
  return Math.max(1, Number.isFinite(value) ? value : 1);
}

function createSeededRandom(seed: string): () => number {
  let state = hashSeed(seed);
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function hashSeed(seed: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
